/**
 * Die **Formatierung** eines **Texts** (CONTEXT.md, Text; ADR 0029): was an
 * einer Stelle gilt, und wie eine Wahl aus der Leiste auf den Inhalt wirkt.
 *
 * **Wo eine Wahl wirkt:** Im Bearbeiten-Modus auf die Markierung — ohne
 * Markierung auf das, was man als Nächstes tippt (Fett & Co.) bzw. auf den
 * ganzen Text (Farbe, Schrift, Größe). Ist der Text nur ausgewählt, auf den
 * ganzen Text; das erledigt hier ein kopfloser TipTap-Editor über demselben
 * Schema wie das Schreibfeld (`canvasTextSchema.ts`).
 */

import { Editor, type JSONContent } from '@tiptap/core'
import { Selection, type EditorState } from '@tiptap/pm/state'
import type { Node as PmNode } from '@tiptap/pm/model'
import type { Step } from '@tiptap/pm/transform'
import type { CanvasTextAlign } from '@/src/renderer/lib/db/models'
import type { CanvasTextHighlightKey } from './canvasText'
import {
  CANVAS_TEXT_DEFAULT_KEY,
  parseCanvasText,
  type CanvasTextMarks,
} from './canvasTextContent'
import {
  canvasTextExtensions,
  canvasTextMarksOf,
  scaleSizesInDoc,
  toCanvasTextHtml,
  type CanvasTextStyleAttr,
} from './canvasTextSchema'

/** Die vier Auszeichnungen, die man an- und ausschaltet. */
export type CanvasTextMarkKind = 'bold' | 'italic' | 'underline' | 'strike'

export const CANVAS_TEXT_MARKS: readonly CanvasTextMarkKind[] = ['bold', 'italic', 'underline', 'strike']

/** Eine Wahl aus der Leiste, die nicht Farbe, Schrift oder Größe ist. */
export type CanvasTextFormatChange =
  | { kind: 'mark'; mark: CanvasTextMarkKind }
  /**
   * `look`: die Form für Neues; Vorhandenes behält seine (siehe
   * `setCanvasHighlight`). Ohne `look` wird wie früher neu gewürfelt.
   */
  | { kind: 'highlight'; color: CanvasTextHighlightKey | null; look?: number }
  | { kind: 'list' }
  | { kind: 'align'; align: CanvasTextAlign }
  /** „Marker wechseln": alle Marker des Texts bekommen neue Formen — immer der ganze Text. */
  | { kind: 'shuffleMarkers' }

/** Die Grundwerte des Texts — was gilt, wo eine Stelle nichts Eigenes trägt. */
export interface CanvasTextBase {
  color: string | null
  font: string | null
  fontSize: number
}

/**
 * Was die Leiste zeigt. Farben, Schriften und Größen als die VERSCHIEDENEN
 * Werte, die vorkommen — mehr als einer heißt „gemischt", wie bei einer
 * Auswahl mehrerer Elemente.
 */
export interface CanvasTextFormat {
  bold: boolean
  italic: boolean
  underline: boolean
  strike: boolean
  /** Die Marker-Farbe; `null` ohne Marker, `undefined` gemischt. */
  highlight: CanvasTextHighlightKey | null | undefined
  list: boolean
  colors: (string | null)[]
  fonts: (string | null)[]
  sizes: number[]
}

/** Was an der Markierung steht, roh — `canvasTextFormatFrom` macht daraus die Anzeige. */
export interface CanvasTextSelectionInfo {
  /** Die Marks am Caret (inkl. der für das nächste Getippte); `null` bei einer Markierung. */
  caret: CanvasTextMarks | null
  /** Die Stellen der Markierung — am Caret die des ganzen Texts (Farbe, Schrift und Größe gelten dann ihm). */
  runs: CanvasTextMarks[]
  list: boolean
}

function distinct<T>(values: readonly T[]): T[] {
  return values.filter((v, i) => values.indexOf(v) === i)
}

const effective = (value: string | undefined, base: string | null) =>
  value === CANVAS_TEXT_DEFAULT_KEY ? null : value ?? base

export function canvasTextFormatFrom(info: CanvasTextSelectionInfo, base: CanvasTextBase): CanvasTextFormat {
  const { caret, runs } = info
  const on = (mark: CanvasTextMarkKind) => (caret ? Boolean(caret[mark]) : runs.length > 0 && runs.every(run => run[mark]))
  const highlights = distinct(runs.map(run => run.highlight ?? null))
  const orBase = <T,>(values: T[], fallback: T) => (values.length > 0 ? values : [fallback])
  return {
    bold: on('bold'),
    italic: on('italic'),
    underline: on('underline'),
    strike: on('strike'),
    highlight: caret ? caret.highlight ?? null : highlights.length === 0 ? null : highlights.length === 1 ? highlights[0] : undefined,
    list: info.list,
    colors: orBase(distinct(runs.map(run => effective(run.color, base.color))), base.color),
    fonts: orBase(distinct(runs.map(run => effective(run.font, base.font))), base.font),
    sizes: orBase(distinct(runs.map(run => run.size ?? base.fontSize)), base.fontSize),
  }
}

/** Die Markierung im Schreibfeld. */
export function canvasTextSelectionOf(state: EditorState): CanvasTextSelectionInfo {
  const { selection, doc } = state
  const empty = selection.empty
  const from = empty ? 0 : selection.from
  const to = empty ? doc.content.size : selection.to
  const runs: CanvasTextMarks[] = []
  let blocks = 0
  let listed = 0
  doc.nodesBetween(from, to, (node, _pos, parent) => {
    if (node.isText) runs.push(canvasTextMarksOf(node.marks))
    if (node.isTextblock) {
      blocks += 1
      if (parent?.type.name === 'listItem') listed += 1
    }
  })
  let list = blocks > 0 && listed === blocks
  if (empty) {
    const $from = selection.$from
    list = false
    for (let depth = $from.depth; depth > 0; depth -= 1) if ($from.node(depth).type.name === 'listItem') list = true
  }
  return {
    caret: empty ? canvasTextMarksOf(state.storedMarks ?? selection.$from.marks()) : null,
    runs,
    list,
  }
}

/** Der ganze Text in Ruhe — gelesen über die Whitelist, ohne Editor. */
export function canvasTextFormatOf(content: string, base: CanvasTextBase): CanvasTextFormat {
  const blocks = parseCanvasText(content)
  const runs = blocks.flatMap(block => (block.kind === 'list' ? block.items.flat() : block.content))
    .flatMap(part => (part.kind === 'text' ? [part.marks] : []))
  const list = blocks.length > 0 && blocks.every(block => block.kind === 'list')
  return canvasTextFormatFrom({ caret: null, runs, list }, base)
}

/** Ein kopfloser Editor über dem Inhalt; `run` formt ihn, heraus kommt das neue HTML. */
function withEditor(content: string, run: (editor: Editor) => void): string {
  const editor = new Editor({ extensions: canvasTextExtensions(), content: toCanvasTextHtml(content), injectCSS: false })
  try {
    run(editor)
    return editor.getHTML()
  } finally {
    editor.destroy()
  }
}

/**
 * Die Liste für den ganzen Text: Ist alles Liste, wird alles wieder Absatz;
 * sonst wird alles EINE Liste. Von Hand statt `toggleBulletList`: Über einen
 * Text, der halb Liste, halb Absatz ist, täte das Kommando je nach Anfang
 * etwas anderes.
 */
function toggleWholeList(editor: Editor): void {
  const blocks: JSONContent[] = editor.getJSON().content ?? []
  const paragraphs: JSONContent[] = []
  for (const block of blocks) {
    if (block.type !== 'bulletList') paragraphs.push(block)
    else for (const item of (block.content ?? []) as JSONContent[]) paragraphs.push(...((item.content ?? []) as JSONContent[]))
  }
  const allList = blocks.length > 0 && blocks.every(block => block.type === 'bulletList')
  editor.commands.setContent({
    type: 'doc',
    content: allList
      ? paragraphs
      : [{ type: 'bulletList', content: paragraphs.map(paragraph => ({ type: 'listItem', content: [paragraph] })) }],
  })
}

/**
 * Eine Wahl auf den GANZEN Text — der Text ist ausgewählt, nicht in Bearbeitung.
 * Eine Auszeichnung, die schon überall gilt, geht überall weg; sonst kommt sie
 * überall hin.
 *
 * @example
 * formatCanvasText('<p>Hallo</p>', { kind: 'mark', mark: 'bold' }) // '<p><strong>Hallo</strong></p>'
 */
export function formatCanvasText(content: string, change: Exclude<CanvasTextFormatChange, { kind: 'align' }>): string {
  if (!content) return content
  return withEditor(content, editor => {
    switch (change.kind) {
      case 'mark': {
        const all = canvasTextFormatOf(content, { color: null, font: null, fontSize: 0 })[change.mark]
        if (all) editor.chain().selectAll().unsetMark(change.mark).run()
        else editor.chain().selectAll().setMark(change.mark).run()
        break
      }
      case 'highlight':
        // Mit `look` nur umfärben — die Formen bleiben (siehe `setCanvasHighlight`).
        if (!change.color || change.look === undefined) editor.chain().selectAll().unsetMark('canvasHighlight').run()
        if (change.color) editor.chain().selectAll().setCanvasHighlight(change.color, change.look).run()
        break
      case 'shuffleMarkers':
        editor.commands.shuffleCanvasMarkers()
        break
      case 'list':
        toggleWholeList(editor)
        break
    }
  })
}

/**
 * Eine Wahl im Schreibfeld — auf die Markierung, ohne sie auf das nächste
 * Getippte (Fett & Co., Marker) bzw. den Absatz am Caret (Liste).
 */
export function applyCanvasTextFormat(editor: Editor, change: Exclude<CanvasTextFormatChange, { kind: 'align' }>): void {
  switch (change.kind) {
    case 'mark':
      editor.chain().toggleMark(change.mark).run()
      break
    case 'highlight':
      if (change.color) editor.chain().setCanvasHighlight(change.color, change.look).run()
      else editor.chain().unsetMark('canvasHighlight').run()
      break
    case 'shuffleMarkers':
      editor.commands.shuffleCanvasMarkers()
      break
    case 'list':
      editor.chain().toggleBulletList().run()
      break
  }
}

/** Was die **Vorschau** im Schreibfeld zeigt: eine Tinte, eine Schrift oder einen Marker an der Markierung. */
export type CanvasTextPreviewChange =
  | { kind: 'color' | 'font'; value: string | number | null }
  | { kind: 'highlight'; color: CanvasTextHighlightKey | null; look?: number }

/**
 * Zeigt eine Farbe oder einen Marker an der Markierung, **ohne Verlaufseintrag**
 * — die Vorschau des Farbwählers (CONTEXT.md, Farbwähler). Gibt die Rücknahme
 * zurück: Sie spielt die Schritte umgekehrt ab, ebenfalls ohne Eintrag, und
 * setzt Markierung und vorgemerkte Marks wie vorher. Danach steht im
 * Editor-Verlauf nichts, was ⌘Z zurücknehmen müsste.
 *
 * Umgekehrte Schritte statt den alten Inhalt zurückzuschreiben: Mark-Schritte
 * verschieben keine Positionen, der Verlauf bildet seine Einträge also
 * unverändert durch sie hindurch ab. Ein Ersetzen des ganzen Dokuments
 * zerschnitte ihn.
 *
 * @param value bei `color` und `font` der Wert der Abweichung (wie `setCanvasTextStyle`)
 */
export function previewCanvasTextFormat(editor: Editor, change: CanvasTextPreviewChange): () => void {
  const { selection, storedMarks } = editor.state
  let steps: Step[] = []
  let docs: PmNode[] = []
  const chain = editor.chain().command(({ tr }) => {
    tr.setMeta('addToHistory', false)
    return true
  })
  const applied = change.kind !== 'highlight'
    ? chain.setCanvasTextStyle(change.kind, change.value)
    : change.color ? chain.setCanvasHighlight(change.color, change.look) : chain.unsetMark('canvasHighlight')
  applied
    .command(({ tr }) => {
      steps = [...tr.steps]
      docs = [...tr.docs]
      return true
    })
    .run()

  return () => {
    if (editor.isDestroyed) return
    const { tr } = editor.state
    for (let i = steps.length - 1; i >= 0; i -= 1) tr.step(steps[i]!.invert(docs[i]!))
    tr.setSelection(Selection.fromJSON(tr.doc, selection.toJSON()))
    tr.setStoredMarks(storedMarks)
    tr.setMeta('addToHistory', false)
    editor.view.dispatch(tr)
  }
}

/**
 * Räumt einen Wert aus dem Text — der neue Grundwert gilt dann überall
 * (ADR 0029). Steht der Wert nirgends, bleibt der Inhalt, wie er ist; ein
 * Klartext wird so nicht nebenbei zu HTML.
 */
export function clearCanvasTextStyle(content: string, attr: CanvasTextStyleAttr): string {
  if (!content.includes(`data-${attr}`)) return content
  return withEditor(content, editor => { editor.commands.clearCanvasTextStyle(attr) })
}

/**
 * Was zu einem neuen Grundwert am **Text** dazugehört: der Inhalt ohne die
 * Abweichungen — im selben Schritt geschrieben (ADR 0029). Leer, wenn es
 * keine gibt oder das Element kein Text ist.
 */
export function clearedCanvasTextStyle(note: { kind: string; content: string }, attr: CanvasTextStyleAttr): { content?: string } {
  if (note.kind !== 'text') return {}
  const content = clearCanvasTextStyle(note.content, attr)
  return content === note.content ? {} : { content }
}

/** Jede eigene Größe im Text mal `factor` — beim Ziehen an den Ecken. */
export function scaleCanvasTextSizes(content: string, factor: number): string {
  if (!content.includes('data-size') || factor === 1) return content
  return withEditor(content, editor => {
    const { tr } = editor.state
    scaleSizesInDoc(tr, factor)
    editor.view.dispatch(tr)
  })
}
